/**
 * Header and navigation behaviors.
 *
 * Progressive enhancement only:
 * - Mega menus are native <details> (click-to-open works without JS);
 *   this script adds "close others", outside click and Escape handling.
 * - The mobile drawer is a native <dialog>: focus trap, Escape and focus
 *   restore are provided by the platform; this script only opens/closes it
 *   and manages drill-down levels and tabs.
 *
 * All handlers use event delegation on document, so theme editor section
 * reloads require no re-initialization.
 */
(function () {
  'use strict';

  // The theme editor re-inserts this script when the header section is
  // reloaded; all handlers are delegated on document, so a single
  // registration keeps working for any re-rendered header markup.
  if (window.SuplementariaHeaderNavInitialized) return;
  window.SuplementariaHeaderNavInitialized = true;

  var MEGA_MENU = '[data-mega-menu]';
  var drillOpeners = new WeakMap();

  /* ------------------------------------------------------------------ *
   * Mega menus (desktop)
   * ------------------------------------------------------------------ */

  // Registro dos details abertos pelo hover intent (origem do estado).
  var hoverOpened = new WeakSet();

  document.addEventListener(
    'toggle',
    function (event) {
      var menu = event.target;
      if (!menu.matches || !menu.matches(MEGA_MENU)) return;
      // aria-expanded explícito só existe com JS ativo; estático ele ficaria
      // dessincronizado do details sem script (o summary nativo já cobre).
      var summary = menu.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', menu.open ? 'true' : 'false');
      if (!menu.open) {
        hoverOpened.delete(menu); // fechado por qualquer mecanismo
        return;
      }
      document.querySelectorAll(MEGA_MENU + '[open]').forEach(function (other) {
        if (other !== menu) other.open = false; // o toggle do outro limpa o registro
      });
    },
    true
  );

  document.addEventListener('click', function (event) {
    document.querySelectorAll(MEGA_MENU + '[open]').forEach(function (menu) {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    var openMenu = document.querySelector(MEGA_MENU + '[open]');
    if (!openMenu) return;
    openMenu.open = false;
    var summary = openMenu.querySelector('summary');
    if (summary) summary.focus();
  });

  /* ------------------------------------------------------------------ *
   * Mega menus: hover-intent híbrido (nunca hover-only).
   * Só com hover preciso (matchMedia) + pointerType mouse; touch/pen
   * seguem no clique/drawer. Abre ~300ms; troca ~175ms; fecha ~400ms.
   * Anti-flicker por atraso adaptativo (menu-aim): abrir/trocar exige
   * permanência revalidada com :hover no disparo — passagem rápida ou
   * trajeto diagonal não abre nem troca; o painel só fecha quando o
   * novo abre. Hover nunca move/restaura foco; foco dentro do painel
   * impede o fechamento. pointerdown cede o controle ao clique nativo.
   * ------------------------------------------------------------------ */

  var HOVER_OPEN_MS = 300;
  var HOVER_SWITCH_MS = 175;
  var HOVER_CLOSE_MS = 400;
  var hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  var hoverTimers = new WeakMap();

  function megaTimers(menu) {
    var timers = hoverTimers.get(menu);
    if (!timers) {
      timers = { open: 0, close: 0 };
      hoverTimers.set(menu, timers);
    }
    return timers;
  }

  function clearMegaTimers(menu) {
    var timers = hoverTimers.get(menu);
    if (!timers) return;
    clearTimeout(timers.open);
    clearTimeout(timers.close);
  }

  document.addEventListener('pointerover', function (event) {
    if (!hoverQuery.matches || event.pointerType !== 'mouse') return;
    if (!event.target.closest) return;
    var menu = event.target.closest(MEGA_MENU);
    if (!menu) return;
    var from = event.relatedTarget;
    if (from && menu.contains(from)) return; // movimento interno ao conjunto

    var timers = megaTimers(menu);
    clearTimeout(timers.close); // reentrar cancela o fechamento
    if (menu.open) return;
    clearTimeout(timers.open);
    var delay = document.querySelector(MEGA_MENU + '[open]') ? HOVER_SWITCH_MS : HOVER_OPEN_MS;
    timers.open = setTimeout(function () {
      if (menu.isConnected && hoverQuery.matches && menu.matches(':hover') && !menu.open) {
        hoverOpened.add(menu); // origem: hover intent
        menu.open = true; // 'toggle' fecha os demais
      }
    }, delay);
  });

  document.addEventListener('pointerout', function (event) {
    if (!hoverQuery.matches || event.pointerType !== 'mouse') return;
    if (!event.target.closest) return;
    var menu = event.target.closest(MEGA_MENU);
    if (!menu) return;
    var to = event.relatedTarget;
    if (to && menu.contains(to)) return; // gatilho ↔ painel não fecha

    var timers = megaTimers(menu);
    clearTimeout(timers.open);
    if (!menu.open) return;
    clearTimeout(timers.close);
    timers.close = setTimeout(function () {
      if (!menu.isConnected || !menu.open) return;
      if (menu.contains(document.activeElement)) return; // foco dentro: mantém
      if (menu.matches(':hover')) return;
      menu.open = false; // passivo: não restaura foco
    }, HOVER_CLOSE_MS);
  });

  // Clique nativo assume o details: cancela intenções pendentes e a
  // origem hover — o estado passa a ser controlado pelo usuário.
  document.addEventListener('pointerdown', function (event) {
    if (!event.target.closest) return;
    var menu = event.target.closest(MEGA_MENU);
    if (!menu) return;
    clearMegaTimers(menu);
    hoverOpened.delete(menu);
  });

  // Sem hover preciso (ex.: notebook híbrido em modo touch): limpa todos
  // os timers e fecha somente os menus abertos por hover — clique/teclado
  // permanecem; menu com foco dentro é preservado e perde a origem hover.
  hoverQuery.addEventListener('change', function () {
    if (hoverQuery.matches) return; // voltar ao hover não abre nada
    document.querySelectorAll(MEGA_MENU).forEach(clearMegaTimers);
    document.querySelectorAll(MEGA_MENU + '[open]').forEach(function (menu) {
      if (!hoverOpened.has(menu)) return;
      if (menu.contains(document.activeElement)) {
        hoverOpened.delete(menu); // preservado; segue por teclado/clique
        return;
      }
      menu.open = false; // fechamento existente; sem tocar no foco
    });
  });

  // Theme Editor: block promocional visível enquanto selecionado.
  document.addEventListener('shopify:block:select', function (event) {
    if (!event.target || !event.target.closest) return;
    var menu = event.target.closest(MEGA_MENU);
    if (menu && !menu.open) menu.open = true; // sem mover o foco do editor
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    if (!event.target || !event.target.closest) return;
    var menu = event.target.closest(MEGA_MENU);
    if (menu && menu.open) menu.open = false; // painel não fica preso
  });

  /* ------------------------------------------------------------------ *
   * Mobile drawer
   * ------------------------------------------------------------------ */

  function resetDrawer(dialog) {
    dialog.querySelectorAll('[data-drill-panel]').forEach(function (panel) {
      panel.hidden = true;
    });
    dialog.querySelectorAll('[data-drill-open]').forEach(function (button) {
      button.setAttribute('aria-expanded', 'false');
    });
  }

  document.addEventListener('click', function (event) {
    var target = event.target;

    var openButton = target.closest('[data-mobile-menu-open]');
    if (openButton) {
      var dialog = document.getElementById(openButton.getAttribute('aria-controls'));
      if (dialog && typeof dialog.showModal === 'function' && !dialog.open) {
        // Garante que o drawer sempre reabra no nível inicial, mesmo se o
        // evento 'close' não tiver sido entregue pelo navegador.
        resetDrawer(dialog);
        dialog.showModal();
      }
      return;
    }

    var closeButton = target.closest('[data-mobile-menu-close]');
    if (closeButton) {
      var parentDialog = closeButton.closest('dialog');
      if (parentDialog) parentDialog.close();
      return;
    }

    var drillOpen = target.closest('[data-drill-open]');
    if (drillOpen) {
      var panel = document.getElementById(drillOpen.getAttribute('aria-controls'));
      if (!panel) return;
      drillOpen.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      drillOpeners.set(panel, drillOpen);
      var back = panel.querySelector('[data-drill-back]');
      if (back) back.focus();
      return;
    }

    var drillBack = target.closest('[data-drill-back]');
    if (drillBack) {
      var backPanel = drillBack.closest('[data-drill-panel]');
      if (!backPanel) return;
      backPanel.hidden = true;
      var opener = drillOpeners.get(backPanel);
      if (!opener || !opener.isConnected) {
        // The original opener may have been replaced by a theme editor
        // reload; recover it through its aria-controls relationship.
        opener = document.querySelector('[data-drill-open][aria-controls="' + backPanel.id + '"]');
      }
      if (opener) {
        opener.setAttribute('aria-expanded', 'false');
        opener.focus();
      }
      return;
    }

    // Click on the dialog backdrop (outside the inner container) closes it.
    if (target.matches && target.matches('dialog[data-mobile-menu]')) {
      target.close();
    }
  });

  // Reset drill levels when the drawer closes so it reopens at level 1.
  document.addEventListener(
    'close',
    function (event) {
      if (event.target.matches && event.target.matches('dialog[data-mobile-menu]')) {
        resetDrawer(event.target);
      }
    },
    true
  );

})();

/* ---------------------------------------------------------------------------
 * Header compacto ao rolar (mobile)
 * O header é sticky: sem isso ele carrega os ~210px inteiros (logo + busca +
 * tira de atalhos) tela abaixo. Ao rolar, a tira sai e a linha do logo
 * encolhe — busca e carrinho continuam sempre à mão.
 * ------------------------------------------------------------------------- */
(function () {
  var header = document.querySelector('.header');
  if (!header || header.dataset.compactBound === 'true') return;
  header.dataset.compactBound = 'true';

  var LIMIAR = 140;
  var pendente = false;

  function avaliar() {
    pendente = false;
    var compacto = window.scrollY > LIMIAR;
    if (compacto !== (header.dataset.compact === 'true')) {
      header.dataset.compact = compacto ? 'true' : 'false';
    }
  }

  window.addEventListener(
    'scroll',
    function () {
      if (pendente) return;
      pendente = true;
      window.requestAnimationFrame(avaliar);
    },
    { passive: true }
  );

  avaliar();
})();


/* Cabeçalho compacto no celular: recolhe a busca ao descer, devolve ao subir.
   Limiar de 24px evita tremer com o toque; abaixo de 120px a busca sempre
   volta, para o topo da página nunca começar escondendo a busca. */
(function () {
  var header = document.querySelector('.header');
  if (!header) return;
  var alvo = header.closest('.shopify-section') || header;
  var ultimo = window.scrollY;
  var pendente = false;

  function avalia() {
    pendente = false;
    var y = window.scrollY;
    var desceu = y > ultimo + 24;
    var subiu = y < ultimo - 24;
    if (y < 120 || subiu) {
      alvo.classList.remove('header--compact');
      ultimo = y;
    } else if (desceu) {
      alvo.classList.add('header--compact');
      ultimo = y;
    }
  }

  window.addEventListener('scroll', function () {
    if (pendente) return;
    pendente = true;
    window.requestAnimationFrame(avalia);
  }, { passive: true });
})();
